<template>
  <div class="flex w-80 flex-col gap-4 px-4 py-3">
    <UiComboboxMultiVee
      name="filterPricingSegmentIDs"
      :label="$t('area.customer-account.field.pricing-segment')"
      :options="segmentOptions"
      :placeholder="$t('term.all')"
      :modelValue="segmentIDs"
      @update:model-value="segmentIDs = $event" />

    <UiComboboxMultiVee
      name="filterCurrencyCodes"
      :label="$t('term.currency')"
      :options="currencyOptions"
      :placeholder="$t('term.all')"
      :modelValue="currencyCodes"
      @update:model-value="currencyCodes = $event" />

    <Toggle
      :modelValue="modifiedOnly"
      :label="$t('W: Kun tilpassede priser')"
      @update:model-value="modifiedOnly = $event" />

    <!-- Business customers only — insurance customers and private customers cannot be internal. -->
    <Toggle
      v-if="tab === 'business'"
      :modelValue="hideInternal"
      :label="$t('area.customer-account.filter.hide-internal')"
      @update:model-value="hideInternal = $event" />

    <!-- Business and insurance customers — private customers have no welcome-email concept. -->
    <Toggle
      v-if="tab === 'business' || tab === 'insurance'"
      :modelValue="welcomeEmailsOnly"
      :label="$t('W: Kun velkomstmails')"
      @update:model-value="welcomeEmailsOnly = $event" />

    <!-- Only where the list has an inactive state to show. Private customers
         are filtered by their own store, which has no such concept. -->
    <Toggle
      v-if="tab !== 'private'"
      :modelValue="isShowingInactive"
      :label="$t('W: Vis kun inaktive')"
      @update:model-value="onToggleInactive" />

    <button
      v-if="isFiltering"
      type="button"
      class="cursor-pointer self-start text-xs font-medium text-action hover:underline"
      @click="resetStore">
      {{ $t('W: Nulstil filtre') }}
    </button>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { useAppStore } from '@/stores/appStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useCustomerPricingFilterStore } from '@/stores/customerPricingFilterStore'

/**
 * The filter menu for all three customer lists.
 *
 * The pricing filters are shared state, so they persist across a tab switch.
 * "Vis inaktive" is not: it already lived on each list's own store, and private
 * customers have no inactive state at all — hence the `tab` prop, which decides
 * both whether that row renders and which store it toggles.
 */
export default {
  name: 'CustomerFilterMenu',

  emits: ['close'],

  props: {
    tab: {
      type: String as PropType<'business' | 'insurance' | 'private'>,
      required: true,
    },
  },

  computed: {
    ...mapWritableState(useCustomerPricingFilterStore, [
      'segmentIDs',
      'currencyCodes',
      'modifiedOnly',
      'hideInternal',
      'welcomeEmailsOnly',
    ]),
    ...mapState(useCustomerPricingFilterStore, ['isFiltering']),
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(usePricingSegmentStore, ['allPricingSegments']),

    isShowingInactive(): boolean {
      return this.stateFilterStore?.listStateFilter === 'inactive'
    },

    /** Which list's active/inactive filter this menu drives, if any. */
    stateFilterStore() {
      if (this.tab === 'business') return useCustomerAccountStore()
      if (this.tab === 'insurance') return useInsuranceCustomerAccountStore()
      return null
    },

    /** Name plus currency, since two segments can share a name across currencies. */
    segmentOptions(): { value: string; text: string }[] {
      return [...this.allPricingSegments]
        .filter((x) => x.activeState === 'active')
        .map((pricingSegment) => ({
          value: pricingSegment.id,
          text: `${pricingSegment.getName(this.currentLocale)} (${pricingSegment.currencyCode})`,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },

    /**
     * Only currencies some active segment actually uses — filtering by one no
     * customer can be on would just return nothing.
     */
    currencyOptions(): { value: string; text: string }[] {
      return [
        ...new Set(
          this.allPricingSegments
            .filter((x) => x.activeState === 'active')
            .map((x) => x.currencyCode)
        ),
      ]
        .sort((a, b) => a.localeCompare(b))
        .map((code) => ({ value: code, text: code }))
    },
  },

  methods: {
    ...mapActions(useCustomerPricingFilterStore, ['resetStore']),

    // Takes the emitted value rather than flipping the current one: the toggle's
    // own modelValue is derived from the store, so flipping independently can
    // desync the two if the component ever re-emits the same value.
    onToggleInactive(isShowingInactive: boolean): void {
      const store = this.stateFilterStore
      if (!store) return
      store.listStateFilter = isShowingInactive ? 'inactive' : 'active'
    },
  },
}
</script>
